import { useEffect, useState } from 'react';
import { FunctionComponent } from 'react';
import './order-form.scss';
import { produce } from 'immer';
import { GetLanguageLabel } from '../shared/tools';
import DIV from '../shared/html-tags/div';
import LABEL from '../shared/html-tags/label';
import { s } from 'framer-motion/dist/types.d-6pKw1mTI';
import { Description, Numbers } from '@mui/icons-material';

export type Props = {
  source: string;
  numberOfLicenses: number;
  selectedLicenseType: string;
  selectedBundle: string;
  selectedModules: string[];
  calculatedPricePS: number;
  calculatedPriceTotal: number;
  calculatedDiscount: number;
  calculatedSpecialDiscount: number;
  specialDiscountName: string;
};

const OrderForm: FunctionComponent<Props> = (props) => {
  const [quoteCreated, setQuoteCreated] = useState<boolean>(false);
  const [payloadData, setPayloadData] = useState<
    | {
        firstName: string;
        lastName: string;
        emailAddress: string;
        accountName: string;
        description: string;
        user1: string;
        opportunityAmountCurrency: string;
        opportunityAmount: number;
        addressStreet: string;
        addressPostalCode: string;
        addressCity: string;
        addressState: string;
        addressCountry: string;
        phoneNumber: string | null;
        datasource: string;
        language: string;
        cPO: string | null;
        cVATID: string | null;
        cNumberOfLicenses: number;
        cSelectedLicenseType: string;
        cSelectedBundle: string;
        cSelectedModules: string[];
        cCalculatedPricePS: number;
        cCalculatedPriceTotal: number;
        cCalculatedDiscount: number;
        cCalculatedSpecialDiscount: number;
        cSpecialDiscountName: string;
        topics: string[];
        cLeadType: string;
      }
    | undefined
  >(undefined);

  const [accountName, setAccountName] = useState<string | undefined>(undefined);
  const [firstName, setFirstName] = useState<string | undefined>(undefined);
  const [lastName, setLastName] = useState<string | undefined>(undefined);
  const [billingAddressStreet, setBillingAddressStreet] = useState<string | undefined>(undefined);
  const [billingAddressCity, setBillingAddressCity] = useState<string | undefined>(undefined);
  const [billingAddressPostalCode, setBillingAddressPostalCode] = useState<string | undefined>(
    undefined,
  );
  const [billingAddressCountry, setBillingAddressCountry] = useState<string>('Deutschland');
  const [emailAddress, setEmailAddress] = useState<string | undefined>(undefined);
  const [vatId, setVatId] = useState<string | undefined>(undefined);
  const [PO, setPO] = useState<string | undefined>(undefined);
  const [dataPrivacyAccepted, setDataPrivacyAccepted] = useState<string>('');

  // Define the API URL and the endpoint for creating a case
  const apiUrl = 'https://crm.2x4.de/api/v1/LeadCapture/1141c19bf259ba723f11dd082006aa8b';

  // Function to create a case
  // ----------------------------------------------------------------
  async function createQuote(type: string, language: string) {
    // ----------------------------------------------------------------

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },

        body: JSON.stringify(
          produce(payloadData, (update) => {
            if (update) {
              update.description = type;
              update.language = language;
              update.cLeadType = type;
            }
          }),
        ),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      } else {
        setQuoteCreated(true);
        setTimeout(() => {
          window.location.href = '/de/order-success';
        }, 1);
      }

      //const responseData = await response.json();
      //console.log('Case created successfully:', responseData);
    } catch (error) {
      console.error('Error creating quote:', error);
    }
  }

  useEffect(() => {
    setPayloadData({
      firstName: firstName || '',
      lastName: lastName || '',
      accountName: accountName || '',
      emailAddress: emailAddress || '',
      opportunityAmountCurrency: 'EUR',
      opportunityAmount: props.calculatedPriceTotal,
      addressStreet: billingAddressStreet || '',
      addressPostalCode: billingAddressPostalCode || '',
      addressCity: billingAddressCity || '',
      addressState: '',
      addressCountry: billingAddressCountry || '',
      phoneNumber: null,
      datasource: props.source,
      language: 'German',
      cPO: PO || null,
      cVATID: vatId || null,
      description: '',
      user1: dataPrivacyAccepted,
      cNumberOfLicenses: props.numberOfLicenses,
      cSelectedLicenseType: props.selectedLicenseType,
      cSelectedBundle: props.selectedBundle,
      cSelectedModules: props.selectedModules,
      cCalculatedPricePS: props.calculatedPricePS,
      cCalculatedPriceTotal: props.calculatedPriceTotal,
      cCalculatedDiscount: props.calculatedDiscount,
      cCalculatedSpecialDiscount: props.calculatedSpecialDiscount,
      cSpecialDiscountName: props.specialDiscountName,
      topics: ['PS Imago Pro'],
      cLeadType: 'Generic',
    });
  }, [
    firstName,
    lastName,
    emailAddress,
    accountName,
    billingAddressStreet,
    billingAddressPostalCode,
    billingAddressCity,
    billingAddressCountry,
    PO,
    vatId,
    props.source,
    dataPrivacyAccepted,
    props.numberOfLicenses,
    props.selectedLicenseType,
    props.selectedModules.length,
    props.calculatedPricePS,
    props.calculatedPriceTotal,
    props.calculatedDiscount,
    props.calculatedSpecialDiscount,
    props.specialDiscountName,
    props.selectedBundle,
    props.selectedModules,
  ]);

  // ----------------------------------------------------------------
  // ----------------------------------------------------------------
  // ----------------------------------------------------------------
  // ----------------------------------------------------------------
  // ----------------------------------------------------------------
  return (
    <div className="form">
      {!quoteCreated && (
        <>
          {/* ======================================================================================== */}
          <div className="form-line">
            <DIV language="de" className="form-label">
              Firma / Behörde / Institution
            </DIV>
            <DIV language="en" className="form-label">
              Company
            </DIV>
            <div className="form-input">
              <input
                className="full-width"
                type="text"
                placeholder={GetLanguageLabel([
                  { language: 'de', label: 'Name der Firma oder Institution' },
                  { language: 'en', label: 'Company Name' },
                ])}
                value={accountName}
                onChange={(e) => {
                  setAccountName(e.currentTarget.value);
                }}
              ></input>
            </div>
          </div>
          {/* ======================================================================================== */}
          <div className="form-line">
            <DIV language="de" className="form-label">
              Dein Name
            </DIV>
            <DIV language="en" className="form-label">
              Your Name
            </DIV>
            <div className="form-input">
              <input
                className="half-width"
                type="text"
                placeholder={GetLanguageLabel([
                  { language: 'de', label: 'Vorname' },
                  { language: 'en', label: 'First Name' },
                ])}
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.currentTarget.value);
                }}
              ></input>
              <input
                className="half-width"
                type="text"
                placeholder={GetLanguageLabel([
                  { language: 'de', label: 'Nachname' },
                  { language: 'en', label: 'Last Name' },
                ])}
                value={lastName}
                onChange={(e) => {
                  setLastName(e.currentTarget.value);
                }}
              ></input>
            </div>
          </div>
          {/* ======================================================================================== */}
          <div className="form-line">
            <DIV language="de" className="form-label">
              Adresse
            </DIV>
            <DIV language="en" className="form-label">
              Address
            </DIV>
            <div className="form-input">
              <textarea
                className="full-width"
                placeholder={'Adresse'}
                value={billingAddressStreet}
                onChange={(e) => {
                  setBillingAddressStreet(e.currentTarget.value);
                }}
              />
            </div>
          </div>

          {/* ======================================================================================== */}
          <div className="form-line">
            <DIV language="de" className="form-label">
              Ort
            </DIV>
            <DIV language="en" className="form-label">
              City
            </DIV>
            <div className="form-input">
              <input
                style={{ width: 'calc(6rem - 0.25rem)' }}
                type="text"
                placeholder={GetLanguageLabel([
                  { language: 'de', label: 'PLZ' },
                  { language: 'en', label: 'ZIP' },
                ])}
                value={billingAddressPostalCode}
                onChange={(e) => {
                  setBillingAddressPostalCode(e.currentTarget.value);
                }}
              ></input>
              <input
                style={{ width: '22rem' }}
                type="text"
                placeholder={GetLanguageLabel([
                  { language: 'de', label: 'Stadt/Ort' },
                  { language: 'en', label: 'City' },
                ])}
                value={billingAddressCity}
                onChange={(e) => {
                  setBillingAddressCity(e.currentTarget.value);
                }}
              ></input>
            </div>
          </div>
          {/* ======================================================================================== */}
          <div className="form-line">
            <DIV language="de" className="form-label">
              Land
            </DIV>
            <DIV language="en" className="form-label">
              Country
            </DIV>
            <div className="form-input">
              <select
                style={{ width: '31.75rem', color: 'gray', fontSize: '1rem' }}
                value={billingAddressCountry}
                onChange={(e) => {
                  setBillingAddressCountry(e.currentTarget.value);
                }}
              >
                <option value="" disabled>
                  {GetLanguageLabel([
                    { language: 'de', label: 'Land auswählen' },
                    { language: 'en', label: 'Select Country' },
                  ])}
                </option>
                <option value="Germany">Deutschland</option>
                <option value="Denmark">Dänemark</option>
                <option value="Iceland">Island</option>
                <option value="Lichtenstein">Lichtenstein</option>
                <option value="Norway">Norwegen</option>
                <option value="Austria">Österreich</option>
                <option value="Switzerland">Schweiz</option>
                <option value="Sweden">Schweden</option>
              </select>

              {/* 
              <input
                className="full-width"
                type="text"
                placeholder={GetLanguageLabel([
                  { language: 'de', label: 'Land' },
                  { language: 'en', label: 'Country' },
                ])}
                value={payloadData.addressCountry}
                onChange={(e) => {
                  setPayloadData(
                    produce(payloadData, (update) => {
                      update.addressCountry = e.currentTarget.value;
                    })
                  );
                }}
              ></input>
              */}
            </div>
          </div>
          {/* ======================================================================================== */}
          <div className="form-line">
            <DIV language="en" className="form-label">
              Email
            </DIV>
            <DIV language="de" className="form-label">
              E-Mail
            </DIV>
            <div className="form-input">
              <input
                className="full-width"
                type="text"
                placeholder={GetLanguageLabel([
                  { language: 'de', label: 'Deine berufliche E-Mail Adresse' },
                  { language: 'en', label: 'Your Company Email' },
                ])}
                value={emailAddress}
                onChange={(e) => {
                  setEmailAddress(e.currentTarget.value);
                }}
              ></input>
            </div>
          </div>
          {/* ======================================================================================== */}
          <div className="form-line">
            <DIV language="de" className="form-label">
              UST ID
            </DIV>
            <DIV language="en" className="form-label">
              VAT ID
            </DIV>
            <div className="form-input">
              <input
                className="full-width"
                type="text"
                placeholder={GetLanguageLabel([
                  { language: 'de', label: 'Umsatzsteuer ID' },
                  { language: 'en', label: 'VAT ID' },
                ])}
                value={vatId}
                onChange={(e) => {
                  setVatId(e.currentTarget.value);
                }}
              ></input>
            </div>
          </div>
          {/* ======================================================================================== */}
          <div className="form-line">
            <DIV language="de" className="form-label">
              Bestellnummer / Zeichen
            </DIV>
            <DIV language="en" className="form-label">
              Purchase Order / Reference
            </DIV>
            <div className="form-input">
              <input
                className="full-width"
                type="text"
                placeholder={GetLanguageLabel([
                  { language: 'de', label: 'Bestellnummer / Zeichen' },
                  { language: 'en', label: 'Purchase Order / Reference' },
                ])}
                value={PO}
                onChange={(e) => {
                  setPO(e.currentTarget.value);
                }}
              ></input>
            </div>
          </div>
          {/* ======================================================================================== */}
          <div className="form-line">
            <div className="form-label"></div>
            <div className="form-input form-privacy">
              <input
                id="privacy"
                type="checkbox"
                checked={dataPrivacyAccepted !== ''}
                onChange={(e) => {
                  setDataPrivacyAccepted(
                    e.currentTarget.checked ? 'Policy Accepted: ' + new Date() : '',
                  );
                }}
              ></input>
              <LABEL language="de" htmlFor="privacy">
                <span className="privacy-text">
                  Ich stimme zu, daß meine personenbezogenen Daten gemäß der{' '}
                </span>
                <a href="/privacy" target="_blank">
                  Datenschutzerklärung
                </a>{' '}
                <span className="privacy-text">erhoben und verarbeitet werden.</span>
              </LABEL>
              <LABEL language="en" htmlFor="privacy">
                I accept that the data above will be processed and stored according to the
                SurveyTester{' '}
                <a href="/privacy" target="_blank">
                  Privacy Policy
                </a>
              </LABEL>
            </div>
          </div>
          <div className="form-line">
            <div className="form-label"></div>
            <div className="form-send">
              <input
                type="button"
                value={GetLanguageLabel([
                  { language: 'de', label: 'Angebot per Email anfordern' },
                  { language: 'en', label: 'Request Proposal by Email' },
                ])}
                onClick={() => {
                  createQuote('GetQuote', 'German');
                }}
                disabled={
                  !(
                    accountName &&
                    firstName &&
                    lastName &&
                    billingAddressStreet &&
                    billingAddressPostalCode &&
                    billingAddressCity &&
                    billingAddressCountry &&
                    emailAddress &&
                    dataPrivacyAccepted !== ''
                  )
                }
              ></input>

              {/* 
              <span className="or">oder</span>

              <input
                type="button"
                value={GetLanguageLabel([
                  { language: "de", label: "Jetzt kostenpflichtig bestellen" },
                  { language: "en", label: "Order Now" },
                ])}
                onClick={() => {
                  createQuote("OrderNow", "German");
                }}
                disabled={
                  !(
                    accountName &&
                    firstName &&
                    lastName &&
                    billingAddressStreet &&
                    billingAddressPostalCode &&
                    billingAddressCity &&
                    billingAddressCountry &&
                    emailAddress &&
                    dataPrivacyAccepted !== ""
                  )
                }
              ></input>
              */}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OrderForm;
